/**
 * Partner Link Card - Shows current partner status and actions
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Heart, UserPlus, Unlink, Mail, Check, X, 
  Loader2, Users, Sparkles
} from 'lucide-react';
import { usePartnerLink } from '@/hooks/usePartnerLink';
import { InvitePartnerDialog } from './InvitePartnerDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface PartnerLinkCardProps {
  relationshipType: string;
}

export function PartnerLinkCard({ relationshipType }: PartnerLinkCardProps) {
  const {
    partnerLink,
    pendingInvite,
    isLoading,
    isLinked,
    hasPendingInvite,
    createInvite,
    acceptPendingInvite,
    declinePendingInvite,
    unlinkPartner
  } = usePartnerLink();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const handleAccept = async () => {
    setIsAccepting(true);
    await acceptPendingInvite();
    setIsAccepting(false);
  };

  const handleDecline = async () => {
    setIsDeclining(true);
    await declinePendingInvite();
    setIsDeclining(false);
  };

  const handleUnlink = async () => {
    setIsUnlinking(true);
    await unlinkPartner();
    setIsUnlinking(false);
    setShowUnlinkDialog(false);
  };

  if (isLoading) {
    return (
      <Card className="border-dashed border-2 border-muted-foreground/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading partner status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show pending invite notification
  if (hasPendingInvite && pendingInvite) {
    return (
      <Card className="border-2 border-secondary/30 bg-gradient-to-br from-secondary/10 to-accent/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-secondary/20">
              <Mail className="w-6 h-6 text-secondary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Partner Invite Received!</h4>
              <p className="text-sm text-muted-foreground mt-1">
                <strong>{pendingInvite.partner_display_name}</strong> wants to connect with you 
                for relationship exercises.
              </p>
              <div className="flex gap-2 mt-4">
                <Button 
                  size="sm" 
                  onClick={handleAccept}
                  disabled={isAccepting || isDeclining}
                  className="gap-1"
                >
                  {isAccepting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Accept
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleDecline}
                  disabled={isAccepting || isDeclining}
                  className="gap-1"
                >
                  {isDeclining ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                  Decline
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show linked partner card
  if (isLinked && partnerLink) {
    return (
      <>
        <Card className="border-2 border-accent/30 bg-gradient-to-br from-accent/10 to-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-accent/20">
                <Users className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-foreground">Connected with {partnerLink.partner_display_name}</h4>
                  <Badge variant="secondary" className="text-xs bg-accent/20 text-foreground">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Linked
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  You can now work on relationship exercises together and track your shared progress.
                </p>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setShowUnlinkDialog(true)}
                  className="mt-3 text-muted-foreground hover:text-destructive gap-1"
                >
                  <Unlink className="w-4 h-4" />
                  Unlink Partner
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <AlertDialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unlink from partner?</AlertDialogTitle>
              <AlertDialogDescription>
                This will disconnect you from {partnerLink.partner_display_name}. 
                You can always send a new invite later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleUnlink}
                disabled={isUnlinking}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isUnlinking ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Unlink
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Show invite prompt
  return (
    <>
      <Card className="border-dashed border-2 border-secondary/20 hover:border-secondary/40 transition-colors">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-secondary/10">
              <Heart className="w-6 h-6 text-secondary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Connect with Your Partner</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Invite your partner to work on exercises together, see each other's progress, 
                and strengthen your relationship.
              </p>
              <Button 
                size="sm" 
                onClick={() => setShowInviteDialog(true)}
                className="mt-3 gap-1"
              >
                <UserPlus className="w-4 h-4" />
                Invite Partner
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <InvitePartnerDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onInvite={createInvite}
        relationshipType={relationshipType}
      />
    </>
  );
}
